// src/components/BookingModal.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { X, ArrowLeft, CreditCard, Check, AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { format, addDays, isSameDay, startOfToday } from "date-fns";
import { de } from "date-fns/locale";
import { formatDateTimeDELong, formatTimeDE } from "../lib/datetime";
import { formatCurrency } from "../lib/utils";
import { COUNTRY_PHONE_CODES, buildFullPhone, isValidPhone, sanitizePhoneInput } from "../lib/phone";
import { focusFormError } from "../hooks/useFormErrorFocus";
import { getVocabulary } from "../lib/terminology";
import { erfasseHerkunft, leseHerkunft } from "../lib/acquisitionTracking";
import { emitTrackingEvent } from "../lib/tracking/runtime";
import { buildBookingSuccessRedirectUrl } from "../lib/tracking/redirect";
import LinkifiedText from "./LinkifiedText";
import { AppSelect } from './ui/app-select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function isValidEmail(value) {
  const email = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function formatBookingMoney(price, currency) {
  const value = Number(price || 0);
  if (!Number.isFinite(value) || value <= 0) return "Kostenlos";
  return formatCurrency(value, currency || "EUR");
}

function servicePriceStartsAt(service) {
  return Number(service?.price || 0) > 0 && service?.price_starts_at === true;
}

function formatServicePriceLabel(service, currency) {
  const label = formatBookingMoney(service?.price, currency);
  return servicePriceStartsAt(service) ? `ab ${label}` : label;
}

function shouldShowServicePrice(service) {
  return service?.show_price !== false;
}

function hasComparePrice(service) {
  const price = Number(service?.price || 0);
  const comparePrice = Number(service?.compare_price || 0);
  return shouldShowServicePrice(service) && Number.isFinite(comparePrice) && comparePrice > price && comparePrice > 0;
}

function extractFeeLabel(text) {
  const match = String(text || "").match(/in Höhe von\s+(.+?)\s+berechnet werden\./i);
  return match ? match[1].trim() : "";
}

function buildPolicyNoticeTexts(policyInfo) {
  const cancellationText = String(policyInfo?.cancellation_text || "").trim();
  const noShowText = String(policyInfo?.no_show_text || "").trim();
  if (!cancellationText && !noShowText) return [];

  const cancellationFee = extractFeeLabel(cancellationText);
  const noShowFee = extractFeeLabel(noShowText);
  if (cancellationText && noShowText && cancellationFee && cancellationFee === noShowFee) {
    const hoursMatch = cancellationText.match(/weniger als\s+(\d+)\s+Stunden/i);
    const hours = hoursMatch ? hoursMatch[1] : "24";
    return [
      `Bei Absagen weniger als ${hours} Stunden vor dem Termin oder bei Nichterscheinen kann ein Ausfallhonorar in Höhe von ${cancellationFee} berechnet werden.`
    ];
  }

  return [cancellationText, noShowText].filter(Boolean);
}

const BOOKING_HORIZON_DAYS = 180;
const BOOKING_WINDOW_DAYS = 5;
const PREFETCH_WINDOW_DAYS = 14;
const PREFETCH_BATCH_SIZE = 3;
const GOAE_BILLING_NOTICE = "Hinweis: Privatärztliche Leistungen werden nach GOÄ abgerechnet. Der Steigerungsfaktor kann, soweit die GOÄ dies zulässt, je nach Leistung und Aufwand bis zum 3,5-Fachen betragen. Die konkrete Abrechnung erfolgt nach der Behandlung.";

export default function BookingModal({
  open,
  onClose,
  orgSlug,
  org,
  service,
  theme,
  locationId,
  locationName,
  autoAssignStaff = true,
  returnPath,
  embedded = false,
  successRedirectUrl = "",
}) {
  // Das Buchungsmodal wird ausschließlich auf öffentlichen Seiten verwendet und darf
  // deshalb nicht vom TerminologyProvider des eingeloggten Bereichs abhängen.
  const vocab = getVocabulary(org?.terminology);
  const [step, setStep] = useState(0);

  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayWindowStart, setDayWindowStart] = useState(() => startOfToday());
  const [searchingEarliest, setSearchingEarliest] = useState(false);
  const [searchProgressDate, setSearchProgressDate] = useState(null);
  const slotsCacheRef = useRef(new Map());
  const slotsRequestSeqRef = useRef(0);
  const earliestSearchSeqRef = useRef(0);
  const prefetchSeqRef = useRef(0);
  const selectedDateKeyRef = useRef("");
  const userDateInteractedRef = useRef(false);
  const autoJumpCompletedRef = useRef(false);
  const [daySlotCounts, setDaySlotCounts] = useState({});
  const [earliestSearchSettled, setEarliestSearchSettled] = useState(false);

  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [holdLoading, setHoldLoading] = useState(false);
  const [holdDeadline, setHoldDeadline] = useState(0);
  const [holdSeconds, setHoldSeconds] = useState(0);
  const holdRef = useRef(null);
  const holdRequestSeqRef = useRef(0);
  const bookingSubmittingRef = useRef(false);

  const [policyInfo, setPolicyInfo] = useState(null);

  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [showVoucherInput, setShowVoucherInput] = useState(false);
  const [duplicateBookingModalOpen, setDuplicateBookingModalOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    notes: "",
    range_type: "day",
  });
  const studioPrivacyText = String(org?.legal_privacy_text || "").trim();
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  const [booking, setBooking] = useState({
    staff: null,
    slot: null,
    client_first_name: "",
    client_last_name: "",
    client_email: "",
    client_phone: "",
    client_phone_country_code: "+49",
    notes: "",
    gift_card_code: "",
    consent_marketing: true,
    consent_gdpr: false,
  });

  const depositAmount = useMemo(() => {
    if (!service?.deposit_enabled) return 0;
    if (service.deposit_type === "fixed") return Number(service.deposit_value || 0);
    const price = Number(service.price || 0);
    const pct = Number(service.deposit_value || 0);
    return Math.round(price * (pct / 100) * 100) / 100;
  }, [service]);

  const showDepositInfo =
    Boolean(service?.deposit_enabled && service?.deposit_required && depositAmount > 0);
  const requiresOnlinePayment =
    Boolean(service?.payment_type === "online" && Number(service?.price || 0) > 0);
  const showPaymentInfo = showDepositInfo || requiresOnlinePayment;
  const [paymentMode, setPaymentMode] = useState("deposit");
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);
  const today = useMemo(() => startOfToday(), []);
  const maxBookableDate = useMemo(() => addDays(today, BOOKING_HORIZON_DAYS - 1), [today]);
  const accentColor = theme?.accent || theme?.primary || "#0071E3";
  const paymentColor = "#34C759";
  const bookingSteps = useMemo(() => {
    const items = autoAssignStaff
      ? [
          { id: "datetime", label: "Termin", step: 1 },
          { id: "details", label: "Daten", step: 2 },
          { id: "confirm", label: "Bestätigung", step: 3 },
        ]
      : [
          { id: "staff", label: "Mitarbeiter", step: 0 },
          { id: "datetime", label: "Termin", step: 1 },
          { id: "details", label: "Daten", step: 2 },
          { id: "confirm", label: "Bestätigung", step: 3 },
        ];
    return items;
  }, [autoAssignStaff]);
  const activeBookingStepIndex = useMemo(() => {
    if (bookingComplete) return bookingSteps.length;
    const exact = bookingSteps.findIndex((item) => item.step === step);
    if (exact >= 0) return exact;
    const next = bookingSteps.findIndex((item) => item.step > step);
    if (next < 0) return bookingSteps.length - 1;
    return Math.max(0, next - 1);
  }, [bookingComplete, bookingSteps, step]);
  const currencyCode = String(org?.currency || service?.currency || "EUR").toUpperCase();
  const serviceImageUrl = String(service?.image_url || "").trim();
  const showGoaeNotice = Boolean(org?.goae_billing_enabled) && service?.goae_enabled !== false;
  const requiresPolicyAcknowledgement =
    Boolean(policyInfo?.cancellation_text || policyInfo?.no_show_text);
  const requiresHonorAcknowledgement = requiresPolicyAcknowledgement;
  const policyNoticeTexts = useMemo(() => buildPolicyNoticeTexts(policyInfo), [policyInfo]);
  const normalizedSuccessRedirectUrl = useMemo(() => {
    return buildBookingSuccessRedirectUrl(successRedirectUrl);
  }, [successRedirectUrl]);

  useEffect(() => {
    if (!open) return;

    holdRequestSeqRef.current += 1;
    if (holdRef.current && !bookingSubmittingRef.current) void releaseHold();

    setStep(autoAssignStaff ? 1 : 0);
    setSelectedDate(today);
    setDayWindowStart(today);
    setSearchingEarliest(false);
    setSearchProgressDate(null);
    slotsCacheRef.current = new Map();
    slotsRequestSeqRef.current += 1;
    earliestSearchSeqRef.current += 1;
    prefetchSeqRef.current += 1;
    selectedDateKeyRef.current = format(today, "yyyy-MM-dd");
    setDaySlotCounts({});
    setEarliestSearchSettled(false);
    userDateInteractedRef.current = false;
    autoJumpCompletedRef.current = false;
    setStaff([]);
    setSlots([]);
    setLoadingStaff(false);
    setPolicyInfo(null);
    setBookingComplete(false);
    setBookingResult(null);
    setShowVoucherInput(false);
    setDuplicateBookingModalOpen(false);
    setWaitlistOpen(false);
    setPolicyAcknowledged(false);
    setWaitlistForm({ client_name: "", client_email: "", client_phone: "", notes: "", range_type: "day" });
    setBooking({
      staff: null,
      slot: null,
      client_first_name: "",
      client_last_name: "",
      client_email: "",
      client_phone: "",
      client_phone_country_code: "+49",
      notes: "",
      gift_card_code: "",
      consent_marketing: true,
      consent_gdpr: false,
    });
    setPaymentMode(showDepositInfo ? "deposit" : (requiresOnlinePayment ? "full" : "none"));

    if (service?.id) {
      if (!autoAssignStaff) loadStaff(service.id);
      loadPolicy(service.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service?.id, locationId, showDepositInfo, requiresOnlinePayment, autoAssignStaff, today]);

  useEffect(() => {
    if (open) return;
    prefetchSeqRef.current += 1;
  }, [open]);

  useEffect(() => {
    if (!open || !selectedDate) return;
    const hasStaffOrAutoAssign = autoAssignStaff || booking.staff?.id;
    const waitingForAutoSearch = !earliestSearchSettled && !userDateInteractedRef.current && !autoJumpCompletedRef.current;
    if (waitingForAutoSearch) return;
    if (service?.id && selectedDate && hasStaffOrAutoAssign) {
      loadSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service?.id, autoAssignStaff, booking.staff?.id, selectedDate, locationId, earliestSearchSettled]);

  const qs = (obj) => {
    const p = new URLSearchParams();
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && `${v}`.trim() !== "") p.set(k, v);
    });
    return p.toString();
  };

  const releaseHold = useCallback(async () => {
    const previous = holdRef.current;
    holdRef.current = null;
    setHoldDeadline(0);
    setHoldSeconds(0);
    if (!previous?.hold_id) return;
    try {
      await fetch(`${API_URL}/api/public/${orgSlug}/slot-hold/${previous.hold_id}`, { method: "DELETE" });
    } catch {
      // The hold expires on the server even if this best-effort release fails.
    }
  }, [orgSlug]);

  const selectSlot = async (slot) => {
    const requestId = ++holdRequestSeqRef.current;
    setHoldLoading(true);
    await releaseHold();
    setBooking((current) => ({ ...current, slot: null }));
    try {
      const response = await fetch(`${API_URL}/api/public/${orgSlug}/slot-hold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: service.id,
          staff_id: autoAssignStaff ? null : booking.staff?.id,
          location_id: locationId || null,
          start_time: slot.start_time,
          end_time: slot.end_time,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || "Termin konnte nicht reserviert werden.");
      if (holdRequestSeqRef.current !== requestId) {
        await fetch(`${API_URL}/api/public/${orgSlug}/slot-hold/${result.hold_id}`, { method: "DELETE" }).catch(() => {});
        return;
      }
      const remaining = Date.parse(result.expires_at) - Date.parse(result.server_now);
      holdRef.current = result;
      setHoldDeadline(Date.now() + Math.max(0, remaining));
      setHoldSeconds(Math.max(0, Math.ceil(remaining / 1000)));
      const staffForSlot = result.staff_id && !autoAssignStaff
        ? (staff.find((person) => person.id === result.staff_id) || { id: result.staff_id })
        : null;
      setBooking((current) => ({ ...current, slot, staff: staffForSlot || current.staff }));
      setStep(2);
    } catch (error) {
      slotsCacheRef.current = new Map();
      toast.error(error.message || "Termin konnte nicht reserviert werden.");
    } finally {
      if (holdRequestSeqRef.current === requestId) setHoldLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      holdRequestSeqRef.current += 1;
      if (!bookingSubmittingRef.current) void releaseHold();
    }
  }, [open, releaseHold]);

  useEffect(() => () => {
    holdRequestSeqRef.current += 1;
    if (holdRef.current && !bookingSubmittingRef.current) void releaseHold();
  }, [releaseHold]);

  useEffect(() => {
    if (!holdDeadline || !open || bookingComplete) return undefined;
    const tick = () => {
      const seconds = Math.max(0, Math.ceil((holdDeadline - Date.now()) / 1000));
      setHoldSeconds(seconds);
      if (seconds === 0 && holdRef.current && !bookingSubmittingRef.current) {
        holdRequestSeqRef.current += 1;
        void releaseHold();
        setBooking((current) => ({ ...current, slot: null }));
        setStep(1);
        slotsCacheRef.current = new Map();
        setSelectedDate((current) => current ? new Date(current) : current);
        toast.error("Die Reservierung ist abgelaufen. Bitte eine Uhrzeit erneut auswählen.");
      }
    };
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [bookingComplete, holdDeadline, open, releaseHold]);

  const getDateKey = useCallback((day) => format(day, "yyyy-MM-dd"), []);

  useEffect(() => {
    selectedDateKeyRef.current = selectedDate ? getDateKey(selectedDate) : "";
  }, [getDateKey, selectedDate]);

  useEffect(() => {
    slotsCacheRef.current = new Map();
    slotsRequestSeqRef.current += 1;
    earliestSearchSeqRef.current += 1;
    prefetchSeqRef.current += 1;
    setDaySlotCounts({});
    setEarliestSearchSettled(false);
    setSlots([]);
  }, [autoAssignStaff, booking.staff?.id, locationId, service?.id]);

  useEffect(() => {
    if (!selectedDate) return;
    setBooking((prev) => (prev.slot ? { ...prev, slot: null } : prev));
    if (holdRef.current) void releaseHold();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const prepareDateChange = useCallback((day) => {
    selectedDateKeyRef.current = day ? getDateKey(day) : "";
    slotsRequestSeqRef.current += 1;
    setSlots([]);
    setLoadingSlots(Boolean(day));
  }, [getDateKey]);

  const stopEarliestSearch = useCallback(() => {
    earliestSearchSeqRef.current += 1;
    setSearchingEarliest(false);
    setEarliestSearchSettled(true);
  }, []);

  const loadPolicy = async (serviceId) => {
    try {
      const q = qs({ service_id: serviceId, location_id: locationId });
      const res = await fetch(`${API_URL}/api/public/${orgSlug}/policy?${q}`);
      if (!res.ok) return;
      const data = await res.json();
      setPolicyInfo(data);
    } catch {
      // optional
    }
  };

  const loadStaff = async (serviceId) => {
    setLoadingStaff(true);
    try {
      const q = qs({ service_id: serviceId, location_id: locationId });
      const res = await fetch(`${API_URL}/api/public/${orgSlug}/staff?${q}`);
      const data = await res.json();
      const staffList = Array.isArray(data) ? data : [];
      setStaff(staffList);
      // Nur bei Auto-Assign direkt zu Datum/Uhrzeit springen; sonst bleibt User auf Mitarbeiter-Auswahl
      if (autoAssignStaff) setStep(1);
    } catch {
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!service?.id || !selectedDate) {
      toast.error("Bitte Service und Datum auswählen");
      return;
    }
    if (!waitlistForm.client_name?.trim()) {
      toast.error("Bitte Name eingeben");
      return;
    }
    if (!waitlistForm.client_phone?.trim()) {
      toast.error("Bitte Telefonnummer eingeben");
      return;
    }
    setWaitlistLoading(true);
    try {
      const preferredDate = format(selectedDate, "yyyy-MM-dd");
      const preferredDateEnd =
        waitlistForm.range_type === "week" ? format(addDays(selectedDate, 6), "yyyy-MM-dd") : null;
      const payload = {
        service_id: service.id,
        staff_id: booking.staff?.id || null,
        preferred_date: preferredDate,
        preferred_date_end: preferredDateEnd,
        location_id: locationId || null,
        client_name: waitlistForm.client_name.trim(),
        client_email: waitlistForm.client_email?.trim() || null,
        client_phone: waitlistForm.client_phone.trim(),
        notes: waitlistForm.notes?.trim() || null,
      };
      const res = await fetch(`${API_URL}/api/public/${orgSlug}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Warteliste fehlgeschlagen");
      }
      toast.success("Sie wurden zur Warteliste hinzugefügt");
      setWaitlistOpen(false);
      setWaitlistForm({ client_name: "", client_email: "", client_phone: "", notes: "", range_type: "day" });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const getSlotsCacheKey = useCallback((day) => {
    const dateKey = getDateKey(day);
    return [
      orgSlug || "",
      service?.id || "",
      locationId || "",
      autoAssignStaff ? "auto" : (booking.staff?.id || ""),
      dateKey,
    ].join("::");
  }, [autoAssignStaff, booking.staff?.id, getDateKey, locationId, orgSlug, service?.id]);

  const hasSlotsCacheForDay = useCallback((day) => {
    if (!day) return false;
    return slotsCacheRef.current.has(getSlotsCacheKey(day));
  }, [getSlotsCacheKey]);

  const fetchSlotsRangeRows = useCallback(async (days) => {
    if (!service?.id || !Array.isArray(days) || days.length === 0) return null;

    const q = qs({
      service_id: service.id,
      staff_id: autoAssignStaff ? undefined : booking.staff?.id,
      dates: days.map((day) => getDateKey(day)).join(","),
      location_id: locationId,
    });

    try {
      const res = await fetch(`${API_URL}/api/public/${orgSlug}/slots-range?${q}`);
      if (!res.ok) return null;
      const data = await res.json();
      const dayRows = Array.isArray(data?.days) ? data.days : [];
      return dayRows.length ? dayRows : null;
    } catch {
      return null;
    }
  }, [autoAssignStaff, booking.staff?.id, getDateKey, locationId, orgSlug, service?.id]);

  const cacheSlotsForDateKey = useCallback((dateKey, nextSlots) => {
    if (!dateKey) return;
    const day = new Date(`${dateKey}T00:00:00`);
    const cacheKey = getSlotsCacheKey(day);
    const normalizedSlots = Array.isArray(nextSlots) ? nextSlots : [];
    slotsCacheRef.current.set(cacheKey, normalizedSlots);
    setDaySlotCounts((prev) => (
      prev[cacheKey] === normalizedSlots.length ? prev : { ...prev, [cacheKey]: normalizedSlots.length }
    ));
  }, [getSlotsCacheKey]);

  const fetchSlotsForDate = useCallback(async (day) => {
    if (!service?.id || !day) return [];

    const dateKey = getDateKey(day);
    const cacheKey = getSlotsCacheKey(day);
    if (slotsCacheRef.current.has(cacheKey)) {
      const cachedSlots = slotsCacheRef.current.get(cacheKey) || [];
      setDaySlotCounts((prev) => (
        prev[cacheKey] === cachedSlots.length ? prev : { ...prev, [cacheKey]: cachedSlots.length }
      ));
      return cachedSlots;
    }

    const rangeRows = await fetchSlotsRangeRows([day]);
    const rangeRow = rangeRows?.find((row) => row?.date === dateKey);
    if (rangeRow) {
      const normalizedSlots = Array.isArray(rangeRow?.slots) ? rangeRow.slots : [];
      cacheSlotsForDateKey(dateKey, normalizedSlots);
      return normalizedSlots;
    }

    const q = qs({
      service_id: service.id,
      staff_id: autoAssignStaff ? undefined : booking.staff?.id,
      date: dateKey,
      location_id: locationId,
    });

    try {
      const res = await fetch(`${API_URL}/api/public/${orgSlug}/slots?${q}`);
      if (!res.ok) {
        slotsCacheRef.current.set(cacheKey, []);
        setDaySlotCounts((prev) => (prev[cacheKey] === 0 ? prev : { ...prev, [cacheKey]: 0 }));
        return [];
      }
      const data = await res.json();
      const normalizedSlots = Array.isArray(data) ? data : (data?.slots || []);
      slotsCacheRef.current.set(cacheKey, normalizedSlots);
      setDaySlotCounts((prev) => (
        prev[cacheKey] === normalizedSlots.length ? prev : { ...prev, [cacheKey]: normalizedSlots.length }
      ));
      return normalizedSlots;
    } catch {
      slotsCacheRef.current.set(cacheKey, []);
      setDaySlotCounts((prev) => (prev[cacheKey] === 0 ? prev : { ...prev, [cacheKey]: 0 }));
      return [];
    }
  }, [
    autoAssignStaff,
    booking.staff?.id,
    cacheSlotsForDateKey,
    fetchSlotsRangeRows,
    getDateKey,
    getSlotsCacheKey,
    locationId,
    orgSlug,
    service?.id,
  ]);

  const fetchSlotsForDateRange = useCallback(async (days) => {
    if (!service?.id || !Array.isArray(days) || days.length === 0) return false;

    const dayRows = await fetchSlotsRangeRows(days);
    if (!dayRows) return false;

    dayRows.forEach((row) => {
      cacheSlotsForDateKey(row?.date, row?.slots);
    });
    return true;
  }, [cacheSlotsForDateKey, fetchSlotsRangeRows, service?.id]);

  const prefetchSlotWindow = useCallback(async (startDay, days = PREFETCH_WINDOW_DAYS) => {
    if (!open || !service?.id || !startDay) return;
    const hasStaffOrAutoAssign = autoAssignStaff || booking.staff?.id;
    if (!hasStaffOrAutoAssign) return;

    const prefetchId = prefetchSeqRef.current + 1;
    prefetchSeqRef.current = prefetchId;
    const daysToPrefetch = [];
    for (let i = 0; i < days; i++) {
      const day = addDays(startDay, i);
      const cacheKey = getSlotsCacheKey(day);
      if (day.getTime() < today.getTime()) {
        setDaySlotCounts((prev) => (prev[cacheKey] === 0 ? prev : { ...prev, [cacheKey]: 0 }));
        continue;
      }
      if (day.getTime() > maxBookableDate.getTime()) break;
      if (hasSlotsCacheForDay(day)) continue;
      daysToPrefetch.push(day);
    }

    if (!daysToPrefetch.length || prefetchSeqRef.current !== prefetchId) return;
    const prefetched = await fetchSlotsForDateRange(daysToPrefetch);
    if (prefetched || prefetchSeqRef.current !== prefetchId) return;

    for (let i = 0; i < daysToPrefetch.length; i += PREFETCH_BATCH_SIZE) {
      if (prefetchSeqRef.current !== prefetchId) return;
      const batch = daysToPrefetch.slice(i, i + PREFETCH_BATCH_SIZE);
      await Promise.all(batch.map((day) => fetchSlotsForDate(day)));
    }
  }, [
    autoAssignStaff,
    booking.staff?.id,
    fetchSlotsForDateRange,
    fetchSlotsForDate,
    getSlotsCacheKey,
    hasSlotsCacheForDay,
    maxBookableDate,
    open,
    service?.id,
    today,
  ]);

  const fetchNextAvailableSlot = useCallback(async () => {
    if (!service?.id) return { date: null, slots: [] };

    const fromDateKey = getDateKey(today);
    const q = qs({
      service_id: service.id,
      staff_id: autoAssignStaff ? undefined : booking.staff?.id,
      from_date: fromDateKey,
      location_id: locationId,
      horizon_days: BOOKING_HORIZON_DAYS,
    });

    const res = await fetch(`${API_URL}/api/public/${orgSlug}/next-available-slot?${q}`);
    if (!res.ok) {
      throw new Error("next_available_failed");
    }
    const data = await res.json();
    const dateKey = data?.date || null;
    const nextSlots = Array.isArray(data?.slots) ? data.slots : [];
    if (dateKey) {
      const day = new Date(`${dateKey}T00:00:00`);
      const cacheKey = getSlotsCacheKey(day);
      slotsCacheRef.current.set(cacheKey, nextSlots);
      setDaySlotCounts((prev) => (
        prev[cacheKey] === nextSlots.length ? prev : { ...prev, [cacheKey]: nextSlots.length }
      ));
    }
    return { date: dateKey, slots: nextSlots };
  }, [autoAssignStaff, booking.staff?.id, getDateKey, getSlotsCacheKey, locationId, orgSlug, service?.id, today]);

  const loadSlots = useCallback(async () => {
    if (!selectedDate) return;
    const requestId = slotsRequestSeqRef.current + 1;
    slotsRequestSeqRef.current = requestId;
    const expectedDateKey = getDateKey(selectedDate);
    setLoadingSlots(true);
    try {
      const nextSlots = await fetchSlotsForDate(selectedDate);
      if (slotsRequestSeqRef.current !== requestId || selectedDateKeyRef.current !== expectedDateKey) return;
      setSlots(nextSlots);
    } finally {
      if (slotsRequestSeqRef.current === requestId && selectedDateKeyRef.current === expectedDateKey) {
        setLoadingSlots(false);
      }
    }
  }, [fetchSlotsForDate, getDateKey, selectedDate]);

  const getNextDays = useCallback((count = BOOKING_WINDOW_DAYS, fromDate = today) => {
    const days = [];
    for (let i = 0; i < count; i++) days.push(addDays(fromDate, i));
    return days;
  }, [today]);

  const visibleWindowDays = useMemo(
  () =>
    getNextDays(BOOKING_WINDOW_DAYS, dayWindowStart).filter(
      (day) => day.getTime() <= maxBookableDate.getTime()
    ),
  [dayWindowStart, getNextDays, maxBookableDate]
);

const canGoPrevWindow = dayWindowStart.getTime() > today.getTime();
const canGoNextWindow = addDays(dayWindowStart, BOOKING_WINDOW_DAYS).getTime() <= maxBookableDate.getTime();

const shiftDayWindow = useCallback((direction) => {
  const step = direction < 0 ? -BOOKING_WINDOW_DAYS : BOOKING_WINDOW_DAYS;
  const lastWindowStart = addDays(maxBookableDate, -(BOOKING_WINDOW_DAYS - 1));
  setDayWindowStart((prev) => {
    const shifted = addDays(prev, step);
    if (shifted.getTime() < today.getTime()) return today;
    if (shifted.getTime() > lastWindowStart.getTime()) return lastWindowStart;
    return shifted;
  });
}, [maxBookableDate, today]);

const dayWindowTitle = useMemo(() => {
  if (visibleWindowDays.length === 0) {
    return format(dayWindowStart, "MMMM yyyy", { locale: de });
  }
  const firstDay = visibleWindowDays[0];
  const lastDay = visibleWindowDays[visibleWindowDays.length - 1];
  const sameMonth = format(firstDay, "yyyy-MM") === format(lastDay, "yyyy-MM");
  if (sameMonth) {
    return format(firstDay, "MMMM yyyy", { locale: de });
  }
  return `${format(firstDay, "d. MMM", { locale: de })} - ${format(lastDay, "d. MMM yyyy", { locale: de })}`;
}, [dayWindowStart, visibleWindowDays]);

useEffect(() => {
  if (!selectedDate) return;
  const isBeforeWindow = selectedDate.getTime() < dayWindowStart.getTime();
  const lastVisibleDay = visibleWindowDays[visibleWindowDays.length - 1];
  const isAfterWindow = lastVisibleDay
    ? selectedDate.getTime() > lastVisibleDay.getTime()
    : false;
  if (isBeforeWindow || isAfterWindow) {
    setDayWindowStart(selectedDate);
  }
}, [dayWindowStart, selectedDate, visibleWindowDays]);

useEffect(() => {
  if (!open || !selectedDate) return;
  const hasStaffOrAutoAssign = autoAssignStaff || booking.staff?.id;
  if (!hasStaffOrAutoAssign) return;

  if (selectedDate.getTime() < today.getTime()) {
    prepareDateChange(today);
    setSelectedDate(today);
    return;
  }
  if (selectedDate.getTime() > maxBookableDate.getTime()) {
    prepareDateChange(maxBookableDate);
    setSelectedDate(maxBookableDate);
    return;
  }
}, [autoAssignStaff, booking.staff?.id, maxBookableDate, open, prepareDateChange, selectedDate, today]);

useEffect(() => {
  if (!open) return;
  const hasStaffOrAutoAssign = autoAssignStaff || booking.staff?.id;
  if (!hasStaffOrAutoAssign) return;
  if (selectedDate) return;

  prepareDateChange(today);
  setSelectedDate(today);
}, [autoAssignStaff, booking.staff?.id, open, prepareDateChange, selectedDate, today]);

const goPrevWindow = useCallback(() => {
  if (!canGoPrevWindow) return;
  userDateInteractedRef.current = true;
  stopEarliestSearch();
  shiftDayWindow(-1);
  const prevWindowStart = addDays(dayWindowStart, -BOOKING_WINDOW_DAYS);
  prefetchSlotWindow(prevWindowStart);
  setSelectedDate((prev) => {
    if (!prev || prev.getTime() >= dayWindowStart.getTime()) {
      prepareDateChange(prevWindowStart);
      return prevWindowStart;
    }
    return prev;
  });
}, [canGoPrevWindow, dayWindowStart, prepareDateChange, prefetchSlotWindow, shiftDayWindow, stopEarliestSearch]);

const goNextWindow = useCallback(() => {
  if (!canGoNextWindow) return;
  userDateInteractedRef.current = true;
  stopEarliestSearch();
  const nextWindowStart = addDays(dayWindowStart, BOOKING_WINDOW_DAYS);
  shiftDayWindow(1);
  prefetchSlotWindow(nextWindowStart);
  setSelectedDate((prev) => {
    const lastVisibleDay = visibleWindowDays[visibleWindowDays.length - 1];
    if (!prev || (lastVisibleDay && prev.getTime() <= lastVisibleDay.getTime())) {
      prepareDateChange(nextWindowStart);
      return nextWindowStart;
    }
    return prev;
  });
}, [canGoNextWindow, dayWindowStart, prepareDateChange, prefetchSlotWindow, shiftDayWindow, stopEarliestSearch, visibleWindowDays]);

const slotSkeleton = useMemo(
  () => Array.from({ length: 12 }, (_, idx) => idx),
  []
);

const getDaySlotDotClass = useCallback((day) => {
  const count = daySlotCounts[getSlotsCacheKey(day)];
  if (count === undefined) return "bg-[#D1D5DB]";
  if (count > 3) return "bg-[#34C759]";
  if (count > 0) return "bg-[#FFCC00]";
  return "bg-[#FF3B30]";
}, [daySlotCounts, getSlotsCacheKey]);

useEffect(() => {
  if (!open || !service?.id) return;
  const hasStaffOrAutoAssign = autoAssignStaff || booking.staff?.id;
  if (!hasStaffOrAutoAssign) return;

  let cancelled = false;
  const searchId = earliestSearchSeqRef.current + 1;
  earliestSearchSeqRef.current = searchId;
  setSearchingEarliest(true);
  setEarliestSearchSettled(false);
  setSearchProgressDate(today);
  const totalDays = Math.max(
    1,
    Math.floor((maxBookableDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)) + 1
  );

  (async () => {
    try {
      const nextAvailable = await fetchNextAvailableSlot();
      if (cancelled || earliestSearchSeqRef.current !== searchId) return;
      if (nextAvailable.date && nextAvailable.slots.length > 0) {
        const earliest = new Date(`${nextAvailable.date}T00:00:00`);
        if (!userDateInteractedRef.current) {
          prepareDateChange(earliest);
          setSelectedDate(earliest);
          setDayWindowStart(earliest);
          setSlots(nextAvailable.slots);
          setLoadingSlots(false);
          autoJumpCompletedRef.current = true;
        }
        setSearchingEarliest(false);
        setEarliestSearchSettled(true);
        prefetchSlotWindow(earliest);
        return;
      }
      if (earliestSearchSeqRef.current === searchId) {
        setSearchingEarliest(false);
        setEarliestSearchSettled(true);
      }
      return;
    } catch {
      if (cancelled || earliestSearchSeqRef.current !== searchId) return;
    }

    const days = getNextDays(totalDays, today);
    for (const day of days) {
      if (cancelled || earliestSearchSeqRef.current !== searchId) return;
      if (userDateInteractedRef.current || autoJumpCompletedRef.current) {
        if (earliestSearchSeqRef.current === searchId) {
          setSearchingEarliest(false);
          setEarliestSearchSettled(true);
        }
        return;
      }

      setSearchProgressDate(day);
      const daySlots = await fetchSlotsForDate(day);
      if (cancelled || earliestSearchSeqRef.current !== searchId) return;
      if (daySlots.length > 0) {
        if (!userDateInteractedRef.current) {
          prepareDateChange(day);
          setSelectedDate(day);
          setDayWindowStart(day);
          setSlots(daySlots);
          setLoadingSlots(false);
          autoJumpCompletedRef.current = true;
        }
        setSearchingEarliest(false);
        setEarliestSearchSettled(true);
        prefetchSlotWindow(day);
        return;
      }
    }
    if (earliestSearchSeqRef.current === searchId) {
      setSearchingEarliest(false);
      setEarliestSearchSettled(true);
    }
  })();

  return () => {
    cancelled = true;
    if (earliestSearchSeqRef.current === searchId) {
      setSearchingEarliest(false);
      setEarliestSearchSettled(true);
    }
  };
}, [
  autoAssignStaff,
  booking.staff?.id,
  fetchNextAvailableSlot,
  fetchSlotsForDate,
  getNextDays,
  maxBookableDate,
  open,
  prepareDateChange,
  prefetchSlotWindow,
  service?.id,
  today,
]);

  const submitBooking = async () => {
    if (!holdRef.current || holdSeconds <= 0) {
      toast.error("Bitte eine freie Uhrzeit auswählen und innerhalb von fünf Minuten buchen.");
      setStep(1);
      return;
    }
    if (!booking.client_first_name || !booking.client_last_name || !booking.client_email) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    if (!isValidEmail(booking.client_email)) {
      toast.error("Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    if (!(booking.client_phone || "").trim()) {
      toast.error("Telefonnummer ist erforderlich für die Buchung.");
      return;
    }
    if (!isValidPhone(booking.client_phone_country_code, booking.client_phone)) {
      toast.error("Bitte eine vollständige Telefonnummer mit passender Ländervorwahl eingeben.");
      setStep(2);
      window.setTimeout(() => focusFormError(document.getElementById("booking-client-phone")), 0);
      return;
    }
    if (!booking.slot) {
      toast.error("Bitte Uhrzeit auswählen");
      return;
    }
    if (!autoAssignStaff && !booking.staff) {
      toast.error("Bitte Mitarbeiter auswählen");
      return;
    }
    if (requiresHonorAcknowledgement && !policyAcknowledged) {
      toast.error(
        requiresPolicyAcknowledgement
          ? "Bitte bestätigen Sie den Hinweis zu Honorar, Absage und Nichterscheinen."
          : "Bitte bestätigen Sie den Honorarhinweis."
      );
      return;
    }

    setBookingLoading(true);
    bookingSubmittingRef.current = true;
    try {
      const fullPhone = buildFullPhone(booking.client_phone_country_code, booking.client_phone);
      const res = await fetch(`${API_URL}/api/public/${orgSlug}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: service.id,
          staff_id: holdRef.current.staff_id,
          hold_id: holdRef.current.hold_id,
          start_time: booking.slot.start_time,
          end_time: booking.slot.end_time,
          client_first_name: booking.client_first_name,
          client_last_name: booking.client_last_name,
          client_email: String(booking.client_email || "").trim(),
          client_phone: fullPhone,
          notes: booking.notes,
          gift_card_code: booking.gift_card_code || null,
          consent_marketing: Boolean(booking.consent_marketing),
          location_id: locationId || null, // <-- WICHTIG
          payment_mode: showDepositInfo ? paymentMode : (requiresOnlinePayment ? "full" : "none"),
          return_url: `${window.location.origin}${returnPath ? returnPath : (window.location.pathname + (window.location.search || ""))}`,
          // Woher die Patientin kam. Steht nur beim allerersten Aufruf in der
          // URL, deshalb wurde es beim Oeffnen gemerkt.
          ...leseHerkunft(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || "Buchung fehlgeschlagen";
        if (res.status === 409 && (msg.includes("bereits einen Termin") || msg.includes("angefragt") || msg.includes("gebucht"))) {
          setDuplicateBookingModalOpen(true);
          return;
        }
        throw new Error(msg);
      }

      const data = await res.json();
      holdRef.current = null;
      setHoldDeadline(0);

      if (data.requires_payment) {
        if (!data.checkout_url) {
          throw new Error("Fehler beim Erstellen der Zahlungsseite");
        }
        if (embedded && typeof window !== "undefined" && window.parent && window.parent !== window) {
          window.parent.postMessage(
            {
              type: "mib:open-checkout",
              checkoutUrl: data.checkout_url,
            },
            "*",
          );
          return;
        }
        window.location.href = data.checkout_url;
        return;
      }

      await emitTrackingEvent(data.tracking_event);
      if (normalizedSuccessRedirectUrl) {
        window.location.href = normalizedSuccessRedirectUrl;
        return;
      }

      setBookingResult(data);
      setBookingComplete(true);
    } catch (e) {
      const msg = e.message || "Buchung fehlgeschlagen";
      if (msg.includes("bereits einen Termin") || msg.includes("angefragt") || msg.includes("gebucht")) {
        setDuplicateBookingModalOpen(true);
      } else {
        toast.error(msg);
      }
    } finally {
      bookingSubmittingRef.current = false;
      setBookingLoading(false);
    }
  };

  if (!open) return null;

  const shellClassName = embedded
    ? "w-full h-[calc(100dvh-2rem)] min-h-0"
    : "fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-2 sm:p-4";
  const viewportClassName = embedded
    ? "w-full h-full max-w-[560px] mx-auto"
    : "w-full max-w-2xl my-auto";
  const cardClassName = embedded
    ? "bg-white rounded-3xl border border-[#E5E5EA] shadow-sm overflow-hidden h-full min-h-0 flex flex-col"
    : "bg-white rounded-[1.75rem] sm:rounded-3xl border-0 shadow-lg overflow-hidden max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)] min-h-0 flex flex-col";

  return (
    <>
    <div className={shellClassName}>
      <div className={viewportClassName}>
        <Card className={cardClassName}>
          <CardContent className="p-0 flex h-full min-h-0 flex-col">
            {/* Header */}
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-[#E5E5EA] flex items-center justify-between gap-3 shrink-0">
              <div className="min-w-0 flex items-center gap-3">
                {serviceImageUrl ? (
                  <img
                    src={serviceImageUrl}
                    alt={service?.name || service?.title || "Leistung"}
                    className="h-14 w-14 shrink-0 rounded-2xl object-cover bg-[#F5F5F7]"
                    loading="lazy"
                  />
                ) : null}
                <div className="min-w-0">
                  <div className="text-xs text-[#86868B] mb-1">{locationName || org?.name || "Standort"}</div>
                  <div className="text-lg font-semibold text-[#1D1D1F] truncate">
                    {service?.name || service?.title || "Termin buchen"}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {step > (autoAssignStaff ? 1 : 0) && !bookingComplete && (
                  <Button
                    variant="ghost"
                    className="btn-ghost"
                    onClick={() => setStep((s) => Math.max(autoAssignStaff ? 1 : 0, s - 1))}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                )}
                <button
                  onClick={onClose}
                  disabled={bookingLoading}
                  className="p-2 rounded-xl hover:bg-[#F5F5F7] transition-colors"
                  aria-label="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {!bookingComplete && (
              <div className="border-b border-[#E5E5EA] px-4 py-3 sm:px-6 shrink-0">
                <div className="flex items-center justify-center gap-2 max-w-lg mx-auto w-full">
                  {bookingSteps.map((item, index) => {
                    const isDone = index < activeBookingStepIndex;
                    const isActive = index === activeBookingStepIndex;
                    return (
                      <div key={item.id} className="flex min-w-0 flex-1 items-center gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors"
                            style={{
                              backgroundColor: isDone || isActive ? accentColor : "#F5F5F7",
                              color: isDone || isActive ? "#FFFFFF" : "#86868B",
                            }}
                          >
                            {isDone ? <Check className="h-3.5 w-3.5" /> : index + 1}
                          </span>
                          <span className={`truncate text-xs font-medium sm:text-sm ${isActive ? "text-[#1D1D1F]" : "text-[#86868B]"}`}>
                            {item.label}
                          </span>
                        </div>
                        {index < bookingSteps.length - 1 && (
                          <div className="h-px flex-1 rounded-full bg-[#E5E5EA]">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: isDone ? "100%" : "0%",
                                backgroundColor: accentColor,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!bookingComplete && holdSeconds > 0 && (
              <div className="border-b border-[#E5E5EA] bg-[#F5F8FF] px-4 py-2 text-center text-sm text-[#1D1D1F]" data-testid="booking-hold-timer">
                Dein Termin ist noch <strong>{String(Math.floor(holdSeconds / 60)).padStart(2, "0")}:{String(holdSeconds % 60).padStart(2, "0")}</strong> Minuten für dich reserviert.
              </div>
            )}

            {/* Content */}
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="p-4 sm:p-6 pb-6">
              {bookingComplete ? (
                <div className="text-center py-6">
                  {bookingResult?.requires_confirmation ? (
                    <>
                      <div className="w-14 h-14 rounded-full bg-[#FF9500] flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-[#1D1D1F]">Anfrage gesendet!</h3>
                      <p className="text-[#86868B] mt-2">
                        Ihre Terminanfrage wurde an den Standort übermittelt und wird geprüft.
                        Sie erhalten eine E-Mail, sobald der Termin bestätigt wurde.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="w-14 h-14 rounded-full bg-[#34C759] flex items-center justify-center mx-auto mb-4">
                        <Check className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-[#1D1D1F]">Buchung bestätigt!</h3>
                      <p className="text-[#86868B] mt-2">Sie erhalten in Kürze eine Bestätigungs-E-Mail.</p>
                    </>
                  )}

                  <div className="mt-6">
                    <Button
                      className="btn-primary"
                      style={{ backgroundColor: accentColor, color: "white" }}
                      onClick={onClose}
                    >
                      Schließen
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Step 1: Staff */}
                  {!autoAssignStaff && step === 0 && (
                    <div className="space-y-4">
                      <h3 className="text-xl font-bold text-[#1D1D1F]">Mitarbeiter wählen</h3>

                      {loadingStaff ? (
                        <div className="flex items-center justify-center py-10">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent" />
                        </div>
                      ) : staff.length === 0 ? (
                        <p className="text-[#86868B]">Keine Mitarbeiter für diesen Service verfügbar.</p>
                      ) : (
                        <div className="space-y-3">
                          {staff.map((s) => (
                            <button
                              key={s.id}
                              onClick={() => {
                                holdRequestSeqRef.current += 1;
                                if (holdRef.current) void releaseHold();
                                setBooking((b) => ({ ...b, staff: s, slot: null }));
                                prepareDateChange(today);
                                setSelectedDate(today);
                                setDayWindowStart(today);
                                setStep(1);
                              }}
                              className="w-full text-left bg-[#F5F5F7] hover:bg-[#E8E8ED] p-4 rounded-2xl transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold"
                                  style={{ backgroundColor: s.color || accentColor }}
                                >
                                  {(s.first_name?.[0] || "M")}{(s.last_name?.[0] || "")}
                                </div>
                                <div>
                                  <div className="font-semibold text-[#1D1D1F]">
                                    {s.first_name} {s.last_name}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Date & Time */}
                  {step === 1 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-bold text-[#1D1D1F]">Datum & Uhrzeit wählen</h3>
                      {searchingEarliest && (
                        <div className="flex items-center gap-2 text-xs text-[#86868B]">
                          <span className="h-3 w-3 rounded-full border border-[#86868B] border-t-transparent animate-spin" />
                          <span>
                            Suche frühestmöglichen Termin
                            {searchProgressDate ? ` (geprüft bis ${format(searchProgressDate, "d. MMM", { locale: de })})` : "..."}
                          </span>
                        </div>
                      )}
                      {!autoAssignStaff && !loadingStaff && !booking.staff && (
                        <p className="text-[#86868B]">
                          Keine Mitarbeiter verfügbar. Bitte wählen Sie einen anderen Service.
                        </p>
                      )}

                      <div className="bg-white rounded-2xl p-4 border border-[#E5E5EA] space-y-3">
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={goPrevWindow}
                            disabled={!canGoPrevWindow}
                            className="h-9 w-9 rounded-full border border-[#E5E5EA] flex items-center justify-center transition-all duration-200 hover:bg-[#F5F5F7] disabled:opacity-40"
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>
                          <div className="font-semibold text-[#1D1D1F]">
                            {dayWindowTitle}
                          </div>
                          <button
                            type="button"
                            onClick={goNextWindow}
                            disabled={!canGoNextWindow}
                            className="h-9 w-9 rounded-full border border-[#E5E5EA] flex items-center justify-center transition-all duration-200 hover:bg-[#F5F5F7] disabled:opacity-40"
                          >
                            <ArrowLeft className="w-4 h-4 rotate-180" />
                          </button>
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-2 scroll-smooth">
                          {visibleWindowDays.map((day) => (
                            <button
                              key={day.toISOString()}
                              onClick={() => {
                                userDateInteractedRef.current = true;
                                stopEarliestSearch();
                                prepareDateChange(day);
                                setSelectedDate(day);
                                prefetchSlotWindow(day);
                              }}
                              className={`flex-shrink-0 w-[calc((100%-2rem)/5)] min-w-[62px] p-3 rounded-xl text-center transform-gpu transition-all duration-200 ease-out ${
                                isSameDay(day, selectedDate)
                                  ? "text-white scale-[1.01] shadow-sm"
                                  : "bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED] hover:-translate-y-[1px]"
                              }`}
                              style={isSameDay(day, selectedDate) ? { backgroundColor: accentColor } : {}}
                            >
                              <span className={`mx-auto mb-1 block h-2 w-2 rounded-full ${getDaySlotDotClass(day)}`} />
                              <div className="text-xs opacity-70">{format(day, "EEE", { locale: de })}</div>
                              <div className="text-lg font-semibold">{format(day, "d")}</div>
                              <div className="text-xs opacity-70">{format(day, "MMM", { locale: de })}</div>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl p-6 border border-[#E5E5EA]">
                        <div className="font-medium text-[#1D1D1F] mb-4">
                          {selectedDate
                            ? `Verfügbare Zeiten am ${format(selectedDate, "d. MMMM", { locale: de })}`
                            : "Frühestmögliche Zeiten werden geladen"}
                        </div>

                        {!selectedDate ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent" />
                          </div>
                        ) : loadingSlots ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 animate-pulse">
                            {slotSkeleton.map((item) => (
                              <div key={`slot-skeleton-${item}`} className="h-[46px] rounded-xl bg-[#F2F2F5]" />
                            ))}
                          </div>
                        ) : slots.length === 0 ? (
                          <div className="space-y-3 py-6">
                            <p className="text-[#86868B] text-center">
                              Keine freien Termine für diesen Tag. Bitte anderes Datum wählen oder Warteliste nutzen.
                            </p>
                            {!autoAssignStaff && staff.length > 0 && (
                              <div className="rounded-2xl border border-[#E5E5EA] bg-[#FAFAFB] p-3 space-y-2">
                                <p className="text-xs font-medium text-[#86868B]">Mitarbeiter an diesem Tag</p>
                                {staff.map((member) => (
                                  <div
                                    key={member.id}
                                    className="flex items-center gap-3 rounded-xl bg-white/70 p-3 opacity-55"
                                    title="Für den gewählten Tag ist kein freier Slot verfügbar"
                                  >
                                    <div
                                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold grayscale"
                                      style={{ backgroundColor: member.color || accentColor }}
                                    >
                                      {(member.first_name?.[0] || "M")}{(member.last_name?.[0] || "")}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="text-sm font-medium text-[#1D1D1F] truncate">
                                        {member.first_name} {member.last_name}
                                      </div>
                                      <div className="text-xs text-[#86868B]">Nicht verfügbar</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex justify-center pt-2">
                              <Button
                                variant="outline"
                                className="btn-secondary"
                                onClick={() => setWaitlistOpen(true)}
                                data-testid="join-waitlist-btn"
                              >
                                Auf Warteliste eintragen
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 transition-opacity duration-200 opacity-100">
                            {slots.filter(Boolean).map((slot, idx) => (
                              <button
                                key={slot?.start_time ?? idx}
                                onClick={() => selectSlot(slot)}
                                disabled={holdLoading}
                                className={`p-3 rounded-xl text-center transform-gpu transition-all duration-200 ease-out ${
                                  booking.slot?.start_time === slot?.start_time
                                    ? "text-white scale-[1.01]"
                                    : "bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED] hover:-translate-y-[1px]"
                                }`}
                                style={booking.slot?.start_time === slot?.start_time ? { backgroundColor: accentColor } : {}}
                              >
                                {formatTimeDE(slot?.start_time)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Details */}
                  {step === 2 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-bold text-[#1D1D1F]">Ihre Daten</h3>

                      {showDepositInfo && (
                        <div
                          className="p-4 rounded-2xl border"
                          style={{
                            backgroundColor: `${paymentColor}10`,
                            borderColor: `${paymentColor}30`,
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <CreditCard className="w-5 h-5" style={{ color: paymentColor }} />
                            <div>
                              <div className="font-medium text-[#1D1D1F]">Zahlung direkt bei Buchung</div>
                              <div className="text-sm text-[#86868B] mt-1">
                                Jetzt fällig: <strong>{formatBookingMoney(depositAmount, currencyCode)}</strong>. Danach werden Sie zur Zahlung weitergeleitet.
                              </div>
                              <div className="text-xs text-[#6B7280] mt-2">
                                Dieser Termin ist erst nach erfolgreicher Zahlung verbindlich gebucht.
                              </div>
                              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() => setPaymentMode("deposit")}
                                  className={`rounded-xl border px-3 py-2 text-sm text-left ${
                                    paymentMode === "deposit"
                                       ? ""
                                      : "border-[#E5E5EA] bg-white"
                                  }`}
                                  style={paymentMode === "deposit" ? { borderColor: paymentColor, backgroundColor: `${paymentColor}10` } : undefined}
                                >
                                  <div className="font-medium text-[#1D1D1F]">Anzahlung</div>
                                  <div className="text-xs text-[#86868B]">Jetzt {formatBookingMoney(depositAmount, currencyCode)}</div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPaymentMode("full")}
                                  className={`rounded-xl border px-3 py-2 text-sm text-left ${
                                    paymentMode === "full"
                                       ? ""
                                      : "border-[#E5E5EA] bg-white"
                                  }`}
                                  style={paymentMode === "full" ? { borderColor: paymentColor, backgroundColor: `${paymentColor}10` } : undefined}
                                >
                                  <div className="font-medium text-[#1D1D1F]">Vollzahlung</div>
                                  <div className="text-xs text-[#86868B]">Jetzt {formatBookingMoney(Number(service?.price || 0), currencyCode)}</div>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Vorname *</Label>
                          <Input
                            value={booking.client_first_name}
                            onChange={(e) => setBooking((b) => ({ ...b, client_first_name: e.target.value }))}
                            className="input-base"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nachname *</Label>
                          <Input
                            value={booking.client_last_name}
                            onChange={(e) => setBooking((b) => ({ ...b, client_last_name: e.target.value }))}
                            className="input-base"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>E-Mail *</Label>
                        <Input
                          type="email"
                          value={booking.client_email}
                          onChange={(e) => setBooking((b) => ({ ...b, client_email: e.target.value }))}
                          className="input-base"
                        />
                        {booking.client_email && !isValidEmail(booking.client_email) && (
                          <p className="text-xs text-red-600">Bitte eine gültige E-Mail-Adresse eingeben.</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="booking-client-phone">Telefon *</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2">
                          <AppSelect
                            aria-label="Ländervorwahl"
                            value={booking.client_phone_country_code || "+49"}
                            onChange={(e) => setBooking((b) => ({ ...b, client_phone_country_code: e.target.value }))}
                            className="input-base h-11 rounded-xl border border-[#E5E5EA] bg-white px-3 text-sm"
                          >
                            {COUNTRY_PHONE_CODES.map((item) => (
                              <option key={item.code} value={item.code}>{item.label}</option>
                            ))}
                          </AppSelect>
                          <Input id="booking-client-phone" type="tel" inputKind="phone" autoComplete="tel"
                            aria-invalid={Boolean(booking.client_phone && !isValidPhone(booking.client_phone_country_code, booking.client_phone)) || undefined}
                            aria-describedby="booking-phone-help"
                            value={booking.client_phone}
                            onChange={(e) => setBooking((b) => ({ ...b, client_phone: sanitizePhoneInput(e.target.value) }))}
                            className={`input-base ${booking.client_phone && !isValidPhone(booking.client_phone_country_code, booking.client_phone) ? '!border-red-500 !ring-2 !ring-red-100' : ''}`}
                            placeholder="z.B. 621 123 456"
                          />
                        </div>
                        <p id="booking-phone-help" className="text-xs text-[#86868B]">Für SMS-Bestätigung und Rückfragen. Vorwahl auswählen oder die vollständige Nummer mit + bzw. 00 eingeben.</p>
                        {booking.client_phone && !isValidPhone(booking.client_phone_country_code, booking.client_phone) && (
                          <p className="text-xs text-red-600">Bitte Telefonnummer vollständig eingeben.</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Organisatorischer Hinweis (optional)</Label>
                        <Input
                          value={booking.notes}
                          onChange={(e) => setBooking((b) => ({ ...b, notes: e.target.value }))}
                          placeholder="z. B. Hinweis zur Erreichbarkeit"
                          className="input-base"
                        />
                      </div>
                      {org?.gift_cards_enabled === true && showPaymentInfo && (
                        <div className="space-y-2">
                          {!showVoucherInput ? (
                            <Button
                              type="button"
                              variant="outline"
                              className="btn-secondary w-full"
                              onClick={() => setShowVoucherInput(true)}
                            >
                              Gutschein hinzufügen
                            </Button>
                          ) : (
                            <>
                              <Label>Gutschein-Code</Label>
                              <Input
                                value={booking.gift_card_code}
                                onChange={(e) => setBooking((b) => ({ ...b, gift_card_code: e.target.value }))}
                                className="input-base"
                              />
                            </>
                          )}
                        </div>
                      )}

                      <Button
                        className="btn-primary sticky bottom-0 w-full"
                        style={{ backgroundColor: accentColor, color: "white" }}
                        disabled={
                          !booking.client_first_name ||
                          !booking.client_last_name ||
                          !isValidEmail(booking.client_email) ||
                          !isValidPhone(booking.client_phone_country_code, booking.client_phone)
                        }
                        onClick={() => setStep(3)}
                      >
                        Weiter zur Bestätigung
                      </Button>
                    </div>
                  )}

                  {/* Step 4: Confirm */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-bold text-[#1D1D1F]">Buchung bestätigen</h3>

                      <div className="space-y-3">
                        <div className="p-4 rounded-2xl bg-[#F5F5F7] flex items-center justify-between">
                          <div>
                            <div className="text-xs text-[#86868B]">Service</div>
                            <div className="font-medium text-[#1D1D1F]">{service?.name || service?.title}</div>
                          </div>
                          {shouldShowServicePrice(service) ? (
                            <div className="text-right">
                              {hasComparePrice(service) ? (
                                <div className="text-xs text-[#86868B] line-through">
                                  {formatBookingMoney(service?.compare_price, currencyCode)}
                                </div>
                              ) : null}
                              <div className="font-semibold text-[#1D1D1F]">{formatServicePriceLabel(service, currencyCode)}</div>
                            </div>
                          ) : null}
                        </div>

                        <div className="p-4 rounded-2xl bg-[#F5F5F7]">
                          <div className="text-xs text-[#86868B]">Termin</div>
                          <div className="font-medium text-[#1D1D1F]">
                            {booking.slot && formatDateTimeDELong(booking.slot.start_time)}
                          </div>
                        </div>

                        <div className="p-4 rounded-2xl bg-[#F5F5F7]">
                          <div className="text-xs text-[#86868B]">Ihre Daten</div>
                          <div className="font-medium text-[#1D1D1F]">
                            {booking.client_first_name} {booking.client_last_name}
                          </div>
                          <div className="text-sm text-[#86868B]">{booking.client_email}</div>
                          {booking.client_phone && (
                            <div className="text-sm text-[#86868B]">
                              {buildFullPhone(booking.client_phone_country_code, booking.client_phone)}
                            </div>
                          )}
                        </div>

                        <label className="flex items-start gap-3 p-4 rounded-2xl bg-[#F5F5F7] cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={Boolean(booking.consent_marketing)}
                            onChange={(e) => setBooking((b) => ({ ...b, consent_marketing: e.target.checked }))}
                          />
                          <div>
                            <div className="font-medium text-[#1D1D1F]">E-Mail Marketing-Einwilligung</div>
                            <div className="text-sm text-[#86868B]">
                              Ich möchte E-Mail-Angebote und Neuigkeiten von {org.name || "dem Standort"} erhalten.
                            </div>
                            <div className="text-xs text-[#86868B] mt-1">
                              Sie können die Einwilligung jederzeit widerrufen.
                            </div>
                          </div>
                        </label>

                        <label className="flex items-start gap-3 p-4 rounded-2xl bg-[#F5F5F7] cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-black"
                            required
                            checked={Boolean(booking.consent_gdpr)}
                            onChange={(e) => setBooking((b) => ({ ...b, consent_gdpr: e.target.checked }))}
                          />
                          <div>
                            <div className="font-medium text-[#1D1D1F]">Datenschutz & Verarbeitung *</div>
                            <div className="text-sm text-[#86868B]">
                              Ich stimme zu, dass meine Daten zur Terminabwicklung gemäß der{" "}
                              <button
                                type="button"
                                className="font-medium text-[#0071E3] underline underline-offset-2 hover:text-[#005BB5]"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setPrivacyModalOpen(true);
                                }}
                              >
                                Datenschutzerklärung
                              </button>{" "}
                              verarbeitet werden.
                            </div>
                          </div>
                        </label>

                        {showGoaeNotice && !requiresPolicyAcknowledgement && (
                          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-[13px] leading-5 text-amber-900">
                            {GOAE_BILLING_NOTICE}
                          </div>
                        )}

                        {requiresHonorAcknowledgement && (
                          <label className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4 accent-[#B7791F]"
                              checked={policyAcknowledged}
                              onChange={(e) => setPolicyAcknowledged(e.target.checked)}
                            />
                            <div className="space-y-2 text-[13px] leading-5 text-amber-900">
                              <div className="flex items-center gap-2 font-medium">
                                <AlertCircle className="h-4 w-4 shrink-0 text-amber-700" />
                                <span>
                                  {requiresPolicyAcknowledgement
                                    ? "Hinweis zu Honorar, Absage & Nichterscheinen"
                                    : "Honorarhinweis"}
                                </span>
                              </div>
                              <div className="text-amber-800">
                                Ich habe die Hinweise gelesen und akzeptiere die Bedingungen.
                              </div>
                              {showGoaeNotice ? <div className="text-amber-900/90">{GOAE_BILLING_NOTICE}</div> : null}
                              {policyNoticeTexts.map((text) => (
                                <div key={text} className="text-amber-900/90">{text}</div>
                              ))}
                            </div>
                          </label>
                        )}

                        {showDepositInfo && paymentMode === "deposit" && (
                          <div
                            className="p-4 rounded-2xl border"
                            style={{
                              backgroundColor: `${paymentColor}10`,
                              borderColor: `${paymentColor}30`,
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5" style={{ color: paymentColor }} />
                                <span className="font-medium text-[#1D1D1F]">Zahlung direkt bei Buchung</span>
                              </div>
                              <span className="font-bold" style={{ color: paymentColor }}>
                                {formatBookingMoney(depositAmount, currencyCode)}
                              </span>
                            </div>
                            <div className="text-xs text-[#86868B] mt-2">
                              Restzahlung: {formatBookingMoney(Number(service?.price || 0) - depositAmount, currencyCode)} vor Ort
                            </div>
                            <div className="text-xs text-[#6B7280] mt-2">
                              Wenn die Zahlung nicht abgeschlossen wird, wird kein Termin gebucht.
                            </div>
                          </div>
                        )}
                        {showDepositInfo && paymentMode === "full" && (
                          <div
                            className="p-4 rounded-2xl border"
                            style={{
                              backgroundColor: `${paymentColor}10`,
                              borderColor: `${paymentColor}30`,
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5" style={{ color: paymentColor }} />
                                <span className="font-medium text-[#1D1D1F]">Zahlung direkt bei Buchung</span>
                              </div>
                              <span className="font-bold" style={{ color: paymentColor }}>
                                {formatBookingMoney(Number(service?.price || 0), currencyCode)}
                              </span>
                            </div>
                            <div className="text-xs text-[#6B7280] mt-2">
                              Dieser Termin ist erst nach erfolgreicher Zahlung verbindlich gebucht.
                            </div>
                          </div>
                        )}

                      </div>

                      <Button
                        className="btn-primary sticky bottom-0 w-full"
                        style={{ backgroundColor: accentColor, color: "white" }}
                        onClick={submitBooking}
                        disabled={bookingLoading || !booking.consent_gdpr || (requiresHonorAcknowledgement && !policyAcknowledged)}
                      >
                        {bookingLoading ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            {showDepositInfo ? "Zur Zahlung..." : "Wird gebucht..."}
                          </span>
                        ) : showDepositInfo && paymentMode === "deposit" ? (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Anzahlung bezahlen & buchen
                          </>
                        ) : showDepositInfo && paymentMode === "full" ? (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Vollzahlung & buchen
                          </>
                        ) : service?.requires_confirmation ? (
                          "Anfrage senden"
                        ) : (
                          "Jetzt verbindlich buchen"
                        )}
                      </Button>

                      <div className="text-xs text-center text-[#86868B]">
                        Mit der Buchung akzeptieren Sie die Buchungsbedingungen von {org.name || "dem Standort"}.
                      </div>
                    </div>
                  )}
                </>
              )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

    {waitlistOpen && (
      <div
        className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => e.target === e.currentTarget && setWaitlistOpen(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-lg w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1D1D1F]">Warteliste</h3>
            <button
              onClick={() => setWaitlistOpen(false)}
              className="p-2 rounded-xl hover:bg-[#F5F5F7] transition-colors"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Für welchen Zeitraum?</Label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="waitlist_range"
                    checked={waitlistForm.range_type === "day"}
                    onChange={() => setWaitlistForm((f) => ({ ...f, range_type: "day" }))}
                    className="rounded"
                  />
                  <span className="text-sm">Nur dieser Tag</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="waitlist_range"
                    checked={waitlistForm.range_type === "week"}
                    onChange={() => setWaitlistForm((f) => ({ ...f, range_type: "week" }))}
                    className="rounded"
                  />
                  <span className="text-sm">Gesamte Woche</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Vor- und Nachname"
                value={waitlistForm.client_name}
                onChange={(e) => setWaitlistForm((f) => ({ ...f, client_name: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="waitlist-client-phone">Telefon *</Label>
              <Input id="waitlist-client-phone" type="tel" inputKind="phone"
                placeholder="+49 123 456789"
                value={waitlistForm.client_phone}
                onChange={(e) => setWaitlistForm((f) => ({ ...f, client_phone: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-2">
              <Label>E-Mail (optional, nur wenn bereits {vocab.person.sg})</Label>
              <Input
                type="email"
                placeholder="name@beispiel.de"
                value={waitlistForm.client_email}
                onChange={(e) => setWaitlistForm((f) => ({ ...f, client_email: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-2">
              <Label>Organisatorischer Hinweis (optional)</Label>
              <Textarea
                value={waitlistForm.notes}
                onChange={(e) => setWaitlistForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="z. B. Hinweis zur Erreichbarkeit"
                className="input-base min-h-[80px]"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setWaitlistOpen(false)}>
                Abbrechen
              </Button>
              <Button className="flex-1 btn-primary" onClick={handleJoinWaitlist} disabled={waitlistLoading}>
                {waitlistLoading ? "Wird gesendet..." : "Auf Warteliste"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}

    <Dialog open={privacyModalOpen} onOpenChange={setPrivacyModalOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Datenschutz</DialogTitle>
        </DialogHeader>
        <LinkifiedText className="text-sm leading-6 text-[#1D1D1F]">
          {studioPrivacyText || "Datenschutzerklärung ist noch nicht hinterlegt."}
        </LinkifiedText>
      </DialogContent>
    </Dialog>

    <AlertDialog open={duplicateBookingModalOpen} onOpenChange={setDuplicateBookingModalOpen}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Termin bereits vorhanden</AlertDialogTitle>
          <AlertDialogDescription>
            Du hast bereits einen Termin zu dieser Uhrzeit angefragt bzw. gebucht.
            Sollte es sich um einen Fehler handeln, bitte melde dich telefonisch beim Standort.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogAction className="btn-primary" onClick={() => setDuplicateBookingModalOpen(false)}>
          OK
        </AlertDialogAction>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
